import { AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import { toast } from "sonner";
import { JobRequest } from "../../models/Jobs/JobRequest";
import { JobTypeLabels } from "../../models/Jobs/JobType";
import JobInfoView from "./JobInfoView";
import { isJobFinished } from "../../models/Jobs/JobActions";

interface AbortJobModalProps {
  open: boolean;
  jobRequest: JobRequest;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

const AbortJobModal: React.FC<AbortJobModalProps> = ({
  open,
  jobRequest,
  onClose,
  onConfirm,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isRemove = isJobFinished(jobRequest);
  const verb = isRemove ? "remove" : "abort";
  const verbPast = isRemove ? "removed" : "aborted";
  const jobLabel = JobTypeLabels[jobRequest.type];

  const getMessage = () =>
    `Are you sure you want to ${verb} ${jobLabel} job?`;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      toast.success(`${jobLabel} job ${verbPast}`);
    } catch (error: any) {
      toast.error(
        error?.message ?? `Failed to ${verb} ${jobLabel} job`,
        { duration: 8000 },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-4" />
            Confirmation required
          </DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="flex flex-col justify-center items-center">
          {getMessage()}
          <JobInfoView jobRequest={jobRequest} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose()} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="size-4 animate-spin" />}
            {isRemove ? "Remove" : "Abort"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AbortJobModal;
